import { MultiPlayer } from "../audio/MultiPlayer.js";
import { Data } from "../model/Data.js";
import { TokenHandler } from "../model/TokenHandler.js";
import { AudioControl } from "./AudioControl.js";
import { EmojiDelegate } from "./EmojiDelegate.js";
import { EmojiSelector } from "./EmojiSelector.js";
import { Footer } from "./Footer.js";
import { MenuBar } from "./MenuBar.js";
import { Seek } from "./Seek.js";
import { Sheet } from "./Sheet.js";
import { Sidebar } from "./Sidebar.js";
import { Timeline } from "./Timeline.js";

export interface HomeOptions {
    container: HTMLElement;
    assetsUrl: string;
    audioContext: AudioContext;
    dataUrl: string;
}

export class Home {
    private _element: HTMLElement;

    private _emojiSelector: EmojiSelector;
    private _timeline: Timeline;
    private _seek: Seek;
    private _emojiDelegate: EmojiDelegate;
    private _audioControl: AudioControl;
    private _data: Data;
    private _sidebar: Sidebar;

    public async create(options: HomeOptions) {
        options = e5.object.merge({

        }, options);

        this._data = new Data({
            url: options.dataUrl,
            assetsUrl: options.assetsUrl,
            audioContext: options.audioContext
        });

        new e5.Tooltip({
            touchSupport: false
        });

        await this._data.load();

        const emojis = this._data.data.signum_emoji;
        const instruments = this._data.data.signum_instrument;

        this._element = document.createElement("div");
        this._element.className = "home";
        this._element.innerHTML = `
        <div class="home__emoji-selector"></div>
        <div class="home__timeline"></div>
        <div class="home__seek"></div>
        <div class="home__audio-control"></div>
        <div class="home__sidebar"></div>
        <div class="home__sheet"></div>
        <div class="home__footer"></div>
        `;

        this._sidebar = new Sidebar({
            container: this._element.querySelector(".home__sidebar"),
        });

        const menuBar = this._sidebar.menu;

        this._emojiSelector = new EmojiSelector({
            container: this._element.querySelector(".home__emoji-selector"),
            emojis: emojis,
            groups: this._data.data.signum_emoji_group,
            melodyPlayer: new MultiPlayer({
                instruments: instruments,
                stopInstruments: true,
                audioContext: options.audioContext,
                onStateChange: (p) => {
                    if (p.state === "playing") {
                        this._timeline.sequence.pause();
                    }
                }
            }),
            onDragStart: (item, actor) => {
                const local = e5.Transform.toLocal(item.element.querySelector(".emoji-player"), actor.pointer.x, actor.pointer.y);
                this._emojiDelegate.start({
                    emoji: item.data,
                    event: actor.pointer.event,
                    pivotX: local.x,
                    pivotY: local.y,
                    speed: 1,
                    source: item,
                    playing: false
                });
            }
        });

        this._timeline = new Timeline({
            container: this._element.querySelector(".home__timeline"),
            instruments: instruments,
            audioContext: options.audioContext,
            onDragStart: (item, actor) => {
                if (this._emojiDelegate.isBusy) {
                    return;
                }

                const local = e5.Transform.toLocal(item.element.querySelector(".emoji-player"), actor.pointer.x, actor.pointer.y);
                const emoji = item.emoji;
                const event = actor.pointer.event;
                const playing = item.playing;
                const speed = item.speed;

                this._emojiDelegate.start({
                    emoji: emoji,
                    event: event,
                    pivotX: local.x,
                    pivotY: local.y,
                    speed: speed,
                    source: null,
                    playing: playing
                });

                /**
                 * Remove it after the delegate has started,
                 * so that the delegate is at the right position.
                 * Otherwise the scroll-width could be altered, which
                 * has an effect on the layout.
                 */
                this._timeline.remove([item], false);
            },
            onChangeState: (p) => {
                if (p.state === "playing") {
                    this._emojiSelector.stopPlaying();
                }
            }
        });

        this._timeline.sequence.onChangeItems.add(() => {
            this._emojiSelector.updateDisableState(this._timeline.sequence.items.map((i) => i.emoji));
        });

        this._emojiDelegate = new EmojiDelegate({
            container: this._element,
            timeline: this._timeline
        });

        this._seek = new Seek({
            container: this._element.querySelector(".home__seek"),
            sequence: this._timeline.sequence as any
        });

        if (true || e5.Plugins.user.hasCap("subscriber")) {
            await this._data.loadEmojiCanvases(emojis);
            const sheet = await new Sheet().create({
                container: this._element.querySelector(".home__sheet"),
                sequence: this._timeline.sequence as any,
                visible: false,
                emojis: emojis,
                menuBar: menuBar,
                collapsable: e5.Plugins.config.isLocal//e5.Plugins.user.hasCap("administrator")
            });
        }

        this._audioControl = new AudioControl({
            container: this._element.querySelector(".home__audio-control"),
            sequence: this._timeline.sequence as any
        });

        if (typeof navigator.share === "function") {
            menuBar.add({
                icon: "fal fa-arrow-up-from-bracket",
                tooltip: "Komposition über ihr Gerät teilen",
                disabled: true,
                onInit: (button) => {
                    this._timeline.sequence.onChangeItems.add(() => {
                        button.disabled = this._timeline.sequence.items.length === 0;
                    });
                },
                onTrigger: () => {
                    const url = window.location.href;
                    const title = "Signum";
                    const text = "Signum";

                    navigator.share({
                        url: url,
                        title: title,
                        text: text
                    });
                }
            });
        }

        // menuBar.add({
        //     icon: "fab fa-twitter",
        //     onClick: () => {
        //         const url = window.location.href;
        //         const text = "Signum";

        //         const shareUrl = "https://twitter.com/intent/tweet?text=" + encodeURIComponent(text) + "&url=" + encodeURIComponent(url) + "&via=signum";
        //         window.open(shareUrl, "_blank");
        //     }
        // });

        menuBar.add({
            icon: "fal fa-envelope",
            tooltip: "Komposition per E-Mail teilen",
            disabled: true,
            onInit: (button) => {
                this._timeline.sequence.onChangeItems.add(() => {
                    button.disabled = this._timeline.sequence.items.length === 0;
                });
            },
            onTrigger: () => {
                const url = window.location.href;
                const recipient = this._data.data.settings.mail_recipient;
                const subject = this._data.data.settings.mail_subject;
                const body = this.parseTemplateString(this._data.data.settings.mail_message, {
                    url: url
                });
                const shareUrl = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                // console.log(shareUrl);
                // window.open(shareUrl); 
                location.href = shareUrl;
            }
        });

        if (false && e5.FullscreenUtil.isSupported) {
            menuBar.add({
                icon: {
                    default: "fal fa-expand",
                    active: "fal fa-compress"
                },
                tooltip: {
                    default: "Fullscreen",
                    active: "Fullscreen beenden"
                },
                onTrigger: (b) => {
                    if (!b.active) {
                        e5.FullscreenUtil.enter(document.documentElement);
                    } else {
                        e5.FullscreenUtil.exit();
                    }
                },
                onInit: (b) => {
                    e5.FullscreenUtil.onChange.add(
                        (active) => { b.active = active; }
                    );
                }
            });
        }

        // menuBar.add({
        //     icon: "fal fa-question",
        //     tooltip: "Hilfe",
        //     onInit: (button) => {

        //     }
        // });

        const tokenHandler = new TokenHandler({
            sequence: this._timeline.sequence,
            emojis: emojis
        });

        this._timeline.add(tokenHandler.parseTokenFromUrl());


        const footer = new Footer({
            container: this._element.querySelector(".home__footer"),
            pages: this._data.data.signum_page
        });

        /**
         * Scroll horizontal with mouse wheel.
         */
        e5.event.on({
            type: "wheel",
            selector: ".base__scroll--horz",
            passive: false,
            listener: (e) => {
                if (e.target.offsetWidth >= e.target.scrollWidth) {
                    return;
                }

                const speed = 20;
                let delta = 0;
                if (e.event.deltaY > 0) {
                    delta = speed;
                } else if (e.event.deltaY < 0) {
                    delta = -speed;
                }
                if (delta) {
                    const min = 0;
                    const max = e.target.scrollWidth - e.target.offsetWidth;
                    const left = e5.Calc.clamp(e.target.scrollLeft + delta, min, max);
                    if (left != e.target.scrollLeft) {
                        e.target.scrollLeft = left;
                        e.event.preventDefault();
                    }
                }
            }
        });

        options.container.appendChild(this._element);

        return this;
    }

    private parseTemplateString(template: string, data: any): string {
        return (template || "").replace(/\${(.*?)}/g, (match, key) => {
            if (data[key] === undefined) {
                return "";
            }
            else if (typeof data[key] === "function") {
                return data[key](match);
            }
            return data[key];
        });
    }
}